import React, { useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Element } from 'react-bulma-components'
import { WorkPeriod } from '../../api/work-periods'
import { getDocIdAsString } from '../../utils/documents'
import { isVirtualWorkPeriod } from '../../utils/work-periods'
import { useMaterializeSingleDayMutation } from '../../queries/work-periods'

interface WorkPeriodLinkProps {
  /** The work period to link to (can be real or virtual). */
  workPeriod: WorkPeriod
  /** Content to render inside the link. */
  children: React.ReactNode
  /** Optional extra className. */
  className?: string
  /** Optional inline styles. */
  style?: React.CSSProperties
  /** Text color passed to the Bulma Element. Defaults to "dark". */
  textColor?: string
}

/**
 * Reusable link component for work periods.
 *
 * - Real WP: renders a standard <Link> to the detail page.
 * - Virtual WP: on click, calls materializeSingleDay to persist the WP,
 *   then navigates to the newly created WP's detail page.
 *   If the backend returns null (no slot for that day), does nothing.
 */
const WorkPeriodLink: React.FC<WorkPeriodLinkProps> = ({
  workPeriod,
  children,
  className,
  style,
  textColor = 'dark',
}) => {
  const navigate = useNavigate()
  const materialize = useMaterializeSingleDayMutation()

  const missionId = getDocIdAsString(workPeriod.mission)

  /** Build the detail page URL for a given (real) work period ID. */
  const buildUrl = (wpId: string): string => `/missions/${missionId}/work-periods/${wpId}`

  /** Handle click on a virtual work period: materialize then navigate. */
  const handleVirtualClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault()
      console.log('handleVirtualClick', e)
      if (materialize.isLoading) return

      const result = await materialize.mutateAsync({
        missionId,
        date: new Date(workPeriod.start.date).toISOString(),
      })

      // The API response is unwrapped by the query client select: result is the data directly
      const materializedWp = result?.data
      if (materializedWp) {
        navigate(buildUrl(getDocIdAsString(materializedWp)))
      }
    },
    [missionId, workPeriod.start.date, materialize, navigate],
  )

  // Virtual work period: render a clickable element that materializes on click
  if (isVirtualWorkPeriod(workPeriod)) {
    return (
      <Element
        renderAs="a"
        textColor={textColor}
        className={className}
        style={{ cursor: materialize.isLoading ? 'wait' : 'pointer', ...style }}
        onClick={handleVirtualClick}
      >
        {children}
      </Element>
    )
  }

  // Real work period: standard router Link
  return (
    <Element
      renderAs={Link}
      to={buildUrl(getDocIdAsString(workPeriod))}
      textColor={textColor}
      className={className}
      style={style}
    >
      {children}
    </Element>
  )
}

export default WorkPeriodLink
